import type { MadinaApiEnvelope } from 'TYPES/madina';
import { AxiosResponse } from 'axios';

/** ERP: OnionPay verify returns this until the server webhook marks the order PAID. */
export const MADINA_PAYMENT_AWAITING_WEBHOOK = 'payment_awaiting_webhook';

type MadinaErrorListItem = string | { code?: string; message?: string };

type MadinaThrownError = Error & { madinaCode?: string; madinaData?: unknown };

export const extractMadinaErrorCode = (errors: MadinaApiEnvelope['errors']): string | undefined => {
  if (!Array.isArray(errors) || errors.length === 0) return undefined;
  const first = errors[0];
  if (typeof first === 'string') return first;
  if (first && typeof first === 'object' && 'code' in first) {
    return String((first as { code?: string }).code ?? '');
  }
  return undefined;
};

const throwMadinaEnvelopeError = <T>(envelope: MadinaApiEnvelope<T>): never => {
  const message =
    typeof envelope.message === 'string'
      ? envelope.message
      : Array.isArray(envelope.errors)
        ? envelope.errors
            .map((item: MadinaErrorListItem) =>
              typeof item === 'string' ? item : item?.code ?? item?.message ?? JSON.stringify(item)
            )
            .join(', ')
        : 'Request failed';
  const err = new Error(message) as MadinaThrownError;
  err.madinaCode = extractMadinaErrorCode(envelope.errors);
  if (envelope.data !== undefined && envelope.data !== null) {
    err.madinaData = envelope.data;
  }
  throw err;
};

export const unwrapMadina = <T>(response: AxiosResponse<MadinaApiEnvelope<T> | T>): T => {
  const payload = response.data;

  if (payload && typeof payload === 'object' && 'success' in payload) {
    const envelope = payload as MadinaApiEnvelope<T>;
    if (!envelope.success) {
      throwMadinaEnvelopeError(envelope);
    }
    return envelope.data;
  }

  return payload as T;
};

export const getMadinaErrorMessage = (error: unknown): string => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const axiosError = error as {
      response?: { data?: MadinaApiEnvelope & { message?: string; errors?: unknown } };
    };
    const data = axiosError.response?.data;
    if (data?.message) return data.message;
    if (data?.errors) {
      if (Array.isArray(data.errors)) {
        return data.errors
          .map((item: MadinaErrorListItem) =>
            typeof item === 'string' ? item : item?.message ?? item?.code ?? JSON.stringify(item)
          )
          .join(', ');
      }
      if (typeof data.errors === 'object') {
        return Object.values(data.errors as Record<string, string | string[]>)
          .flat()
          .filter(Boolean)
          .join(', ');
      }
    }
  }
  if (error instanceof Error) return error.message;
  return 'Something went wrong';
};

const isAwaitingWebhookCode = (code: string | undefined): boolean =>
  code === MADINA_PAYMENT_AWAITING_WEBHOOK;

/** OnionPay: verify 422 / payment_awaiting_webhook + PENDING → keep polling GET payment-status. */
export const isMadinaPaymentVerifyAwaitingWebhook = (error: unknown): boolean => {
  const madinaCode =
    error instanceof Error ? (error as MadinaThrownError).madinaCode : undefined;

  if (madinaCode === MADINA_PAYMENT_AWAITING_WEBHOOK) {
    return true;
  }

  const message = getMadinaErrorMessage(error).toLowerCase();

  if (madinaCode === 'payment_verify_failed' && /awaiting|webhook/.test(message)) {
    return true;
  }

  if (
    /awaiting payment-received webhook|awaiting webhook|webhook confirmation|payment_awaiting_webhook/.test(
      message
    )
  ) {
    return true;
  }

  if (typeof error === 'object' && error !== null && 'response' in error) {
    const axiosError = error as {
      response?: {
        status?: number;
        data?: MadinaApiEnvelope;
      };
    };
    const status = axiosError.response?.status;
    const data = axiosError.response?.data;
    const code = extractMadinaErrorCode(data?.errors);

    if (code === MADINA_PAYMENT_AWAITING_WEBHOOK) {
      return true;
    }

    if (status === 422 && isAwaitingWebhookCode(code)) {
      return true;
    }

    if (status === 422 && /awaiting|webhook/.test(message)) {
      return true;
    }
  }

  return false;
};
